import { computed, defineComponent, onMounted, ref } from 'vue';
import { useTagListStore } from '@/store/dictionary/tag-list.store';
import { storeToRefs } from 'pinia';
import { TagListItem, TagListParamsRequest } from '@/models/dictionary/tag-list.model';
import { SegmentListItem } from '@/models/dictionary/segment-list.model';
import { useSegmentListStore } from '@/store/dictionary/segment-list.store';
import { debounce } from 'lodash';
import HintComponent from '@/components/hint/hint';
import { documentListFilterFormRef, useDocumentListFilterStore } from '@/store/documents/document-filter-list.store';
import { DOCUMENT_HINT_MAP } from '@/constants/documents/document-hint.constant';
import { useDocumentListStore } from '@/store/documents/document-list.store';
import { useDocumentListFilter } from '@/hooks/documents/document-list-filter.composables';
import { DocumentListFilterProperties } from '@/models/documents/document-list-filter.model';
import { useCountryListStore } from '@/store/dictionary/country-list.store';
import { useDocumentListFilterFormRules } from '@/hooks/documents/document-list-filter-form.rules';
import { DocumentHintNames } from '@/models/documents/document-hint-names.model';
import { useConditionListStore } from '@/store/dictionary/condition-list.store';
import { ConditionListValueProperty } from '@/models/dictionary/condition-list.model';
import { useEnvStore } from '@/store/env.store';
import {
  useDocumentModerationFilterStatusListStore,
} from '@/store/dictionary/document-moderation-filter-status-list.store';
import { usePermissionsStore } from '@/store/permissions.store';
import { tagListToTagIdList } from '@/mappers/dictionary/tag-list-to-tag-id-list.mapper';
import { segmentListToSegmentIdList } from '@/mappers/dictionary/segment-list-to-segment-id-list.mapper';

export default defineComponent({
  name: 'DocumentFilterList',
  components: { HintComponent },
  setup() {
    const MIN_SEARCH_LENGTH = 2;
    const searchTagIdListText = ref<string>('');
    const searchSegmentIdListText = ref<string>('');

    const { WL_SLUG } = storeToRefs(useEnvStore());
    const { documentsPermission } = storeToRefs(usePermissionsStore());

    const { whiteLabelIdList } = useDocumentListFilter();
    const { clearConditionValidation, documentListFilterFormRules } = useDocumentListFilterFormRules();

    const tagListStore = useTagListStore();
    const { isTagListLoading, tagList } = storeToRefs(tagListStore);
    const { getTagList, setTagList } = tagListStore;

    const segmentListStore = useSegmentListStore();
    const { segmentList, isSegmentListLoading } = storeToRefs(segmentListStore);
    const { getSegmentList, setSegmentList } = segmentListStore;

    const documentListStore = useDocumentListStore();
    const { isDocumentListLoading } = storeToRefs(documentListStore);

    const documentListFilterStore = useDocumentListFilterStore();
    const {
      documentListFilterFormData,
      isFilterTagIdListNotEmpty,
      isFilterSegmentIdListNotEmpty,
      isFilterCountryListNotEmpty,
    } = storeToRefs(documentListFilterStore);
    const {
      setTagIdListCondition,
      setSegmentIdListCondition,
      setCountryListCondition,
      setMinTotalAmountEur,
      setMaxTotalAmountEur,
    } = documentListFilterStore;

    const countryListStore = useCountryListStore();
    const { countryList, isCountryListLoading } = storeToRefs(countryListStore);
    const { getCountryList } = countryListStore;

    const conditionListStore = useConditionListStore();
    const { isConditionListLoading, conditionList, excludeAndConditionList } = storeToRefs(conditionListStore);
    const { getConditionList } = conditionListStore;

    const documentModerationFilterStatusListStore = useDocumentModerationFilterStatusListStore();
    const {
      isDocumentModerationFilterStatusListLoading,
      excludeAllDocumentModerationFilterStatusList,
    } = storeToRefs(documentModerationFilterStatusListStore);
    const { getDocumentModerationFilterStatusList } = documentModerationFilterStatusListStore;

    const tagOptionList = computed<TagListItem[]>(() => {
      const selectedTagList: TagListItem[] = documentListFilterFormData.value.tagList;
      const selectedTagIdSet = new Set<string>(tagListToTagIdList(selectedTagList));

      return [
        ...selectedTagList,
        ...tagList.value.filter(({ tagId }: TagListItem) => !selectedTagIdSet.has(tagId)),
      ];
    });

    const segmentOptionList = computed<SegmentListItem[]>(() => {
      const selectedSegmentList: SegmentListItem[] = documentListFilterFormData.value.segmentList;
      const selectedSegmentIdSet = new Set<string>(segmentListToSegmentIdList(selectedSegmentList));

      return [
        ...selectedSegmentList,
        ...segmentList.value.filter(({ id }: SegmentListItem) => !selectedSegmentIdSet.has(id)),
      ];
    });

    const noDataTagIdListText = computed<string>(() => {
      if (searchTagIdListText.value.length > 0 && searchTagIdListText.value.length < MIN_SEARCH_LENGTH) {
        return `Enter at least ${MIN_SEARCH_LENGTH} characters`;
      }

      return 'No data';
    });

    const noDataSegmentIdListText = computed<string>(() => {
      if (searchSegmentIdListText.value.length > 0 && searchSegmentIdListText.value.length < MIN_SEARCH_LENGTH) {
        return `Enter at least ${MIN_SEARCH_LENGTH} characters`;
      }

      return 'No data';
    });

    const isGetDocumentModerationFilterStatusListAllowed = computed<boolean>(() =>
      documentsPermission.value.isGetDocumentModerationFilterStatusListAllowed);

    const loadTagIdList = debounce(async (query: string): Promise<void> => {
      if (query.length < MIN_SEARCH_LENGTH) {
        setTagList([]);

        return;
      }

      await getTagList({
        object: TagListParamsRequest.PLAYER,
        filterList: { wlIdList: whiteLabelIdList.value, name: query },
        isForceUpdate: true,
      });
    }, 300);

    const remoteTagIdListMethod = (query: string): void => {
      const value: string = query.trim();
      searchTagIdListText.value = value;
      loadTagIdList(value);
    };

    const changeTagIdListVisible = (visible: boolean): void => {
      if (!visible) {
        searchTagIdListText.value = '';
        setTagList([]);
      }
    };

    const loadSegmentIdList = debounce(async (query: string): Promise<void> => {
      if (query.length < MIN_SEARCH_LENGTH) {
        setSegmentList([]);

        return;
      }

      getSegmentList({
        filterList: { wlIdList: whiteLabelIdList.value, isPublished: true, name: query },
        isForceUpdate: true,
      });
    }, 300);

    const remoteSegmentsIdListMethod = (query: string): void => {
      const value: string = query.trim();
      searchSegmentIdListText.value = value;
      loadSegmentIdList(value);
    };

    const changeSegmentIdListVisible = (visible: boolean): void => {
      if (!visible) {
        searchSegmentIdListText.value = '';
        setSegmentList([]);
      }
    };

    const changeTagIdList = (value: TagListItem[]): void => {
      if (!value.length) {
        clearConditionValidation(DocumentListFilterProperties.TAG_ID_LIST_CONDITION);
        setTagIdListCondition('');
        setTagList([]);

        return;
      }

      if (!documentListFilterFormData.value.tagIdListCondition) {
        setTagIdListCondition(ConditionListValueProperty.OR);
      }
    };

    const changeSegmentList = (value: SegmentListItem[]): void => {
      if (!value.length) {
        clearConditionValidation(DocumentListFilterProperties.SEGMENT_ID_LIST_CONDITION);
        setSegmentIdListCondition('');

        return;
      }

      if (!documentListFilterFormData.value.segmentIdListCondition) {
        setSegmentIdListCondition(ConditionListValueProperty.OR);
      }
    };

    const loadDocumentModerationFilterStatusListOnVisibleChange = (visible: boolean): void => {
      if (!visible || excludeAllDocumentModerationFilterStatusList.value.length) {
        return;
      }

      getDocumentModerationFilterStatusList(WL_SLUG.value);
    };

    const changeCountryList = (value: string[]): void => {
      if (!value.length) {
        clearConditionValidation(DocumentListFilterProperties.COUNTRY_LIST_CONDITION);
        setCountryListCondition('');

        return;
      }

      if (!documentListFilterFormData.value.countryListCondition) {
        setCountryListCondition(ConditionListValueProperty.OR);
      }
    };

    const loadCountryListOnVisibleChange = (visible: boolean): void => {
      if (!visible || countryList.value.length) {
        return;
      }

      getCountryList();
    };

    onMounted(() => {
      getConditionList(WL_SLUG.value);
    });

    return {
      isFilterTagIdListNotEmpty,
      isFilterSegmentIdListNotEmpty,
      isFilterCountryListNotEmpty,
      isTagListLoading,
      tagOptionList,
      documentListFilterFormData,
      segmentOptionList,
      isSegmentListLoading,
      isDocumentListLoading,
      documentListFilterFormRef,
      DocumentListFilterProperties,
      changeTagIdList,
      changeSegmentList,
      DOCUMENT_HINT_MAP,
      DocumentHintNames,
      noDataTagIdListText,
      remoteTagIdListMethod,
      changeTagIdListVisible,
      noDataSegmentIdListText,
      remoteSegmentsIdListMethod,
      changeSegmentIdListVisible,
      countryList,
      isCountryListLoading,
      loadCountryListOnVisibleChange,
      changeCountryList,
      documentListFilterFormRules,
      conditionList,
      excludeAndConditionList,
      isConditionListLoading,
      loadDocumentModerationFilterStatusListOnVisibleChange,
      isDocumentModerationFilterStatusListLoading,
      excludeAllDocumentModerationFilterStatusList,
      setMinTotalAmountEur,
      setMaxTotalAmountEur,
      isGetDocumentModerationFilterStatusListAllowed,
    };
  },
});
